import { useState, type FormEvent } from 'react';
import { DetailDrawer, StatusDot, token } from '@bp-files/ui';
import { SpaceAccessFieldset, type Access } from '@/components/Users/SpaceAccessFieldset';
import { USERS_COPY } from '@/components/Users/users.copy';
import { messageFrom } from '@bp-files/client';
import {
  setMemberGrants,
  type GrantInput,
  type GrantMode,
  type SpaceSummary,
  type Member,
} from '@/lib/api';

/**
 * Set one member's space access — USR-4, PERM-1's scope and PERM-1a's level.
 *
 * **One three-state picker per Active space**, replacing the checkbox that used
 * to be the whole model. Access-at-all and read-versus-write are two different
 * facts, and a checkbox can only carry one of them; a checkbox plus a toggle
 * would be two controls per row to keep in sync, with the second meaningless
 * whenever the first is off. One picker with three positions is the same
 * decision said once.
 *
 * `Select` from `@bp-files/ui` rather than a new composition. It is the
 * workspace's established answer for a small closed enum applied immediately
 * (`RetentionCell` in the folder view), and — the part that is not merely
 * consistency — **its popup portals to `document.body`**, which is what lets it
 * open inside `DetailDrawer` at all. A composition of our own would be `Select`
 * plus three copy strings, and `packages/ui` takes its strings as props.
 *
 * Built on `DetailDrawer`, which is already a modal dialog with a focus trap,
 * Escape handling, and focus restore. Nothing here re-implements any of that.
 *
 * **Only Active spaces are offered, and the caller filters them.** A `Requested`
 * space has no space yet (CON-6) and a `Severed` one has lost it (CON-14). A
 * member may still hold a grant on such a space — the API leaves those alone on
 * save, precisely because this editor cannot show them.
 *
 * **What this component decides is what to offer, never what is allowed.** The
 * disabled `Read + write` below is a courtesy; SEC-5 puts the decision in the
 * API, which stores that level on a observer space rather than refusing it.
 */

export interface GrantEditorProps {
  open: boolean;
  member: Member;
  /** The agency's **Active** spaces. The caller filters; this renders. */
  spaces: readonly SpaceSummary[];
  onClose: () => void;
  /** Called after a successful save, so the table can refetch (never patch). */
  onSaved: () => void;
}

export function GrantEditor({ open, member, spaces, onClose, onSaved }: GrantEditorProps) {
  /**
   * Seeded once, on mount.
   *
   * That is sufficient because the caller mounts this per member and keys it by
   * their id, so editing a second member is a remount rather than a prop
   * change. Re-seeding from an effect instead would be a cascading render —
   * and the one bug it would guard against is the one the `key` already
   * removes: a second member inheriting the first's ticks, which is a grant
   * nobody chose.
   */
  const [chosen, setChosen] = useState<ReadonlyMap<string, GrantMode>>(
    () => new Map(member.grants.map((chip) => [chip.spaceId, chip.mode])),
  );
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | undefined>();

  function set(spaceId: string, access: Access): void {
    setChosen((held) => {
      const next = new Map(held);
      if (access === 'none') next.delete(spaceId);
      else next.set(spaceId, access);
      return next;
    });
  }

  /**
   * `void`-returning, with the async work in a nested function — a submit
   * handler that returns a promise is a floating promise as far as React is
   * concerned.
   */
  function submit(event: FormEvent): void {
    event.preventDefault();
    if (pending) return;
    void save();
  }

  async function save(): Promise<void> {
    setPending(true);
    setError(undefined);

    try {
      // Ordered by the list rather than by the order they were changed, so the
      // request body is a function of what is on screen and a test can assert
      // it. A space left at `No access` is **absent** rather than carrying a
      // level: absence is the third state, and sending one would make it
      // indistinguishable from `read` on the wire.
      const grants: GrantInput[] = spaces.flatMap((space) => {
        const mode = chosen.get(space.id);
        return mode === undefined ? [] : [{ spaceId: space.id, mode }];
      });
      await setMemberGrants(member.id, grants);
      onSaved();
      onClose();
    } catch (caught) {
      // PERM-2's refusal on an admin names the remedy — remove their admin
      // rights first — which no generic string can.
      setError(messageFrom(caught, USERS_COPY.grants.failed));
    } finally {
      setPending(false);
    }
  }

  return (
    <DetailDrawer
      open={open}
      onClose={onClose}
      title={USERS_COPY.grants.title}
      closeLabel={USERS_COPY.grants.close}
    >
      <form onSubmit={submit} className="flex flex-col gap-5">
        <p className="text-sm" style={{ color: token('bp-text-muted') }}>
          {USERS_COPY.grants.intro}
        </p>

        <p className="text-sm font-medium">{member.name}</p>

        {spaces.length === 0 ? (
          <p className="text-sm" style={{ color: token('bp-text-muted') }}>
            {USERS_COPY.grants.noSpaces}
          </p>
        ) : (
          <SpaceAccessFieldset
            spaces={spaces}
            chosen={chosen}
            onChange={set}
            legend={USERS_COPY.grants.legend}
          />
        )}

        {error === undefined ? null : (
          <p role="alert" className="flex items-start gap-2 text-sm text-danger">
            <StatusDot tone="alert" />
            {error}
          </p>
        )}

        <div className="flex items-center gap-2">
          <button
            type="submit"
            disabled={pending}
            className="border px-3 py-1.5 text-xs font-medium transition-colors disabled:opacity-60"
            // `bp-on-accent` (white), not `bp-blue-text` — the readme's own
            // line for solid blue buttons. `bp-blue-text` is under 4:1 against
            // this fill, short of WCAG AA's 4.5:1 for text this size.
            style={{
              borderColor: token('bp-blue'),
              background: token('bp-blue-deep'),
              color: token('bp-on-accent'),
              borderRadius: token('radius-md'),
            }}
          >
            {pending ? USERS_COPY.grants.saving : USERS_COPY.grants.save}
          </button>

          <button
            type="button"
            onClick={onClose}
            className="border px-3 py-1.5 text-xs font-medium transition-colors"
            style={{
              borderColor: token('bp-border-strong'),
              color: token('bp-text'),
              borderRadius: token('radius-md'),
            }}
          >
            {USERS_COPY.grants.cancel}
          </button>
        </div>
      </form>
    </DetailDrawer>
  );
}
